import React from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import { useSmoothMarkerPosition } from 'features/Responder/features/Tours/features/TourMap/hooks/useSmoothMarkerPosition';

const toLatLng = (loc = {}) => {
  const lat = Number(loc.lat ?? loc.latitude);
  const lng = Number(loc.lng ?? loc.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  return null;
};

const getTimestamp = (loc) => {
  const raw = loc?.timestamp ?? loc?.time ?? loc?.ts ?? loc?.updatedAt;
  if (raw == null) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const pickLatestLocation = (person) => {
  const candidates = [];
  const pushCandidate = (loc) => {
    const latLng = toLatLng(loc);
    if (!latLng) return;
    candidates.push({ loc, latLng });
  };

  if (Array.isArray(person?.locations) && person.locations.length) {
    pushCandidate(person.locations[person.locations.length - 1]);
  }
  pushCandidate(person?.location);
  pushCandidate(person?.lastStationaryLoc);

  if (!candidates.length) return { loc: null, latLng: null };

  let best = candidates[0];
  for (let i = 1; i < candidates.length; i += 1) {
    const current = candidates[i];
    const bestTs = getTimestamp(best.loc);
    const currentTs = getTimestamp(current.loc);
    if (currentTs != null && (bestTs == null || currentTs > bestTs)) {
      best = current;
    }
  }
  return { loc: best.loc, latLng: best.latLng };
};

const PersonnelMarker = ({ person, onlineIcon, offlineIcon }) => {
  const { loc: latestLocation, latLng: targetLatLng } = pickLatestLocation(person);
  const smoothPosition = useSmoothMarkerPosition(targetLatLng);
  if (!smoothPosition) return null;

  const timestampValue = getTimestamp(latestLocation);
  const timestamp =
    timestampValue != null ? new Date(timestampValue).toLocaleString() : 'No timestamp';

  return (
    <Marker position={smoothPosition} icon={person.clockedIn ? onlineIcon : offlineIcon}>
      <Tooltip className="responder-tooltip" direction="top" offset={[0, -10]} opacity={0.8}>
        <div>
          <div>Personnel #{person.userId ?? person.id}</div>
          <div>{person.clockedIn ? 'Clocked In' : 'Offline'}</div>
          <div>Last Update: {timestamp}</div>
        </div>
      </Tooltip>
    </Marker>
  );
};

/**
 * Render personnel markers with status tooltips.
 */
const PersonellLayer = ({ personnel, onlineIcon, offlineIcon }) => {
  if (!personnel) return null;
  return Object.values(personnel).map((person, index) => (
    <PersonnelMarker
      key={person?.userId ?? person?.id ?? index}
      person={person}
      onlineIcon={onlineIcon}
      offlineIcon={offlineIcon}
    />
  ));
};

export default PersonellLayer;
